/**
 * Estructura del contenido comercial del sitio público, y los datos que **no
 * dependen del idioma**.
 *
 * El texto vive en `src/i18n/locales/<idioma>/marketing.ts`. Aquí quedan las
 * interfaces que ese texto tiene que cumplir y las tres cosas que serían un
 * error duplicar por idioma:
 *
 *  - `precios`: son decisiones de negocio, no traducciones. Si vivieran en cada
 *    diccionario, cambiar una tarifa exigiría acordarse de todos los idiomas y
 *    el primero que se olvidara publicaría un precio viejo. `content.test.ts`
 *    los fija contra lo ya publicado.
 *  - `insightChart`: el reparto de la gráfica. Un 80/20 que dijera 80/20 en
 *    español y 75/25 en inglés sería un defecto, no una traducción.
 *  - `partnerLogos`: rutas de archivo.
 */

/* ── Sección "insight" ── */

/** Reparto que ilustra la dona. Suma 100. */
export const insightChart = {
  passiveLeadsPct: 80,
  scannedLeadsPct: 20,
} as const;

export interface InsightCopy {
  headline: string;
  question: string;
  answer: string;
  /** Etiqueta bajo el número grande de la dona. */
  donutLabel: string;
  /** Leyendas de la gráfica. Reciben el porcentaje ya calculado. */
  legendPassive: (pct: number) => string;
  legendScanned: (pct: number) => string;
  /** Descripción de la gráfica para lectores de pantalla. */
  chartAlt: (pct: number) => string;
}

/* ── Flujo de valor: antes, durante y después del evento ── */

export interface FlowBlock {
  title: string;
  body: string;
}

export interface FlowStage {
  /** Identificador estable, independiente del idioma: es la clave de React,
   *  el `id` del tab y lo que guarda el estado del selector. */
  id: string;
  step: string;
  shortLabel: string;
  title: string;
  image: string;
  imageAlt: string;
  blocks: FlowBlock[];
  note?: string;
}

/* ── Soluciones por tipo de cliente ── */

export interface SolutionItem {
  id: string;
  title: string;
  body: string;
}

export interface SolutionGroup {
  id: string;
  audience: string;
  image: string;
  imageAlt: string;
  items: SolutionItem[];
}

/* ── Planes ──
 *
 * Los cuatro planes publicados. Los dos primeros se segmentan por tipo de
 * cliente (marcas / organizadores) y los dos últimos por duración del evento
 * (1 día / 3 días); por eso cada plan declara sus propias variantes en vez de
 * que el componente asuma una sola dimensión de selector.
 */

export interface PlanVariant {
  /** Identificador estable de la variante. **No se traduce**: es la clave de
   *  React, la llave en `precios` y lo que viaja al formulario de contacto. */
  id: string;
  /** Etiqueta del selector: 'Marcas' / 'Organizadores' / '1 día' / '3 días'. */
  label: string;
  priceNote?: string;
  description?: string;
  features: string[];
}

export interface Plan {
  /** Identificador estable del plan. **No se traduce** (ver `PlanVariant.id`). */
  id: string;
  name: string;
  featured?: boolean;
  variants: PlanVariant[];
  ctaLabel: string;
  /** CTA secundario; siempre lleva a agendar demo. */
  secondaryCtaLabel?: string;
}

/**
 * Precios publicados, por plan y variante. `null` = el plan es gratuito.
 *
 * ⚠️ Si cambian, se cambian aquí y en ningún otro lado.
 */
export const precios: Record<string, Record<string, string | null>> = {
  'free-pass': { marcas: null, organizadores: null },
  'smart-match': { marcas: '$2,999.00', organizadores: '$5,999.00' },
  'smart-partner': { 'un-dia': '$8,689.00', 'tres-dias': '$17,709.00' },
  'solo-brand': { 'un-dia': '$1,399.00', 'tres-dias': '$3,000.00' },
};

/** Precio de una variante, o `null` si es gratis. `undefined` si no existe. */
export function precioDe(planId: string, variantId: string): string | null | undefined {
  return precios[planId]?.[variantId];
}

/* ── Tabla comparativa ── */

export interface ComparisonRow {
  /** Identificador estable de la fila (clave de React). */
  id: string;
  feature: string;
  soloBrand: string;
  smartPartner: string;
  /** `true` cuando Solo Brand no incluye la característica (se marca visualmente). */
  soloBrandMissing?: boolean;
}

/* ── Aliados ── */

export const partnerLogos = [
  '/recursos/logos-aliados/2.png',
  '/recursos/logos-aliados/5.png',
  '/recursos/logos-aliados/dat.png',
  '/recursos/logos-aliados/mexi.png',
  '/recursos/logos-aliados/misu.png',
  '/recursos/logos-aliados/muje.png',
  '/recursos/logos-aliados/on.png',
  '/recursos/logos-aliados/riza.png',
  '/recursos/logos-aliados/vaximed.png',
];
